import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import CloseButton from '@/components/close-button'
import { DeleteError, DeleteRed } from '@/components/icons'
import { createModal, useModalize } from '@/components/modalize'
import { breakpoints, colors, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'

const Wrapper = styled.div`
  background: ${colors.white};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  height: 100%;
  max-height: 98vh;
  padding: 40px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 32px;
  }
`

const Title = styled.h3`
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: ${fontWeights.bold};
  line-height: 32px;
  margin: 24px 0 12px;
  width: 100%;
  text-align: center;
  justify-content: center;
`

const Text = styled.p`
  margin-bottom: 24px;
  color: ${colors.grey48};
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`
const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  width: 100%;
  max-width: 370px;
  height: 100%;
  margin: 0 auto;
`

const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 20px;
  width: 100%;
  height: 48px;
  max-width: 176px;
  background-color: ${colors.white};
  color: ${colors.blue16};
  border: 1.5px solid ${colors.grey88};
  border-radius: 6px;
  flex-grow: 1;
  cursor: pointer;

  :hover {
    opacity: 0.6;
  }
`

const DeleteButton = styled(Button)`
  background-color: ${colors.red96};
  color: ${colors.red64};
  border: 1.5px solid ${colors.red88};
`

const StyledDelete = styled(DeleteRed)`
  margin-right: 12px;
`

const DeleteModal = ({ name, timelineRoomId, history }) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const handleDelete = async () => {
    const { response } = await dispatch.chat.deleteTimeline(timelineRoomId)
    if (response.ok) {
      history.replace(`/chat`)
      close()
    }
  }

  return (
    <Wrapper>
      {isMobile && <CloseButton width={14} height={14} top={29} right={21} onClick={close} />}
      <DeleteError />
      <Title>{t('b.chat.delete_modal.title', 'Are you sure you want to delete this chat?')}</Title>
      <Text>
        <Trans name={name} i18nKey="b.chat.delete_modal.text">
          Your conversation with <span>{{ name }}</span> will be permanently removed. This cannot be
          undone and you won’t be able to recover any messages or files.
        </Trans>
      </Text>
      <ButtonContainer>
        <Button onClick={close}>{t('common.cancel', 'Cancel')}</Button>
        <DeleteButton onClick={handleDelete}>
          <StyledDelete />
          {t('common.delete', 'Delete')}
        </DeleteButton>
      </ButtonContainer>
    </Wrapper>
  )
}

DeleteModal.displayName = 'DeleteModal'

export const showDeleteModal = createModal(DeleteModal, {
  width: 'auto',
  maxWidth: '440px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  tabletFullscreen: true
})
